<script>
import { mapActions } from "vuex";

export default {
  name: "OrderPattern",
  props: {
    /**
     * 受注種別
     */
    orderType: {
      type: String,
      default: "collection"
    },
    /**
     * 排出事業者ID
     */
    emissionId: {
      type: Number,
      default: null
    },
    /**
     * 排出事業場ID
     */
    locationId: {
      type: Number,
      default: null
    },

  },
  computed: {
    /**
     * 検索を実施できるかを返す.
     * @return {number}
     */
    canSearch () {
      return this.emissionId;
    },
  },
  watch: {
    /**
     * 排出事業者IDを監視
     * @param newValue
     * @param oldValue
     */
    emissionId (newValue, oldValue) {
      const currentValue = newValue || null;
      const previousValue = oldValue || null;
      if (currentValue && currentValue !== previousValue) {
        this.handleSearch();
      }
    },
    /**
     * 排出事業場IDの監視
     */
    locationId () {
      this.handleSearch();
    },
  },
  data() {
    return {
      /* キーワード */
      keyword: null,
      isComposition: false,
      /* 回収履歴 */
      patterns: [],
      /* テーブルヘッダ */
      tableHeader: [
        {
          value: "name",
          text: "パターン名\r\nマニ区分",
          width: "30%",
        },
        {
          value: "item_detail",
          text: "取扱品目\r\n荷姿",
          width: "15%",
        },
        {
          value: "route_name",
          text: "排出事業場\r\nルート\r\n処分事業場",
          width: "45%",
        },
        {
          value: "actions",
          text: "",
          width: "10%",
          align: "center"
        }
      ],
    }
  },
  created() {
    this.handleSearch();
  },
  methods: {
    ...mapActions('DriverMasterEntryPattern', {
      fetchEntryPatterns: 'index',
      fetchEntryPattern: 'show',
    }),
    reflectionButtonClickEvent (item) {
      this.fetchEntryPattern({id: item.id}).then((content) => {
        this.$emit("reflect:pattern", content);
      })
    },
    keywordClearEvent () {
      this.keyword = null;
      this.handleSearch();
    },
    enterKeydownEvent () {
      if (this.isComposition) {
        return;
      }
      this.handleSearch();
    },
    /**
     *  IME などのテキスト変換システムが新しい変換セッションを開始した時に発火
     */
    compositionStartEvent () {
      this.isComposition = true
    },
    /**
     * IME などのテキスト編集システムが現在の編集セッションを完了またはキャンセルした時に発火
     */
    compositionEndEvent () {
      this.isComposition = false
      this.handleSearch();
    },
    handleSearch() {
      this.patterns = []
      if (!this.canSearch) {
        return;
      }
      this.search();
    },
    search () {
      const conditions = {
        emission_id: this.emissionId,
        emission_location_id: this.locationId,
        keyword: this.keyword,
        order_type: this.orderType,
      }
      this.fetchEntryPatterns({ conditions: conditions }).then((content) => {
        this.patterns = content.entry_patterns
      });
    },
  },
}
</script>

<template>
  <v-container v-if="canSearch">
    <v-card flat elevation="1">
      <v-card-title>
        <span class="v-label theme--light" style="font-size: 16px;">登録パターン&emsp;</span>
        <v-text-field
          v-model="keyword"
          append-icon="mdi-magnify"
          clearable
          dense
          hide-details="auto"
          outlined
          persistent-placeholder
          placeholder="パターン名・品目・ルートから検索"
          style="font-size: 0.8rem"
          @click:clear="keywordClearEvent"
          @keydown.enter="enterKeydownEvent"
          @compositionstart="compositionStartEvent"
          @compositionend="compositionEndEvent"
        />
      </v-card-title>
      <v-data-table
        :headers="tableHeader"
        :items="patterns"
        :items-per-page="-1"
        disable-sort
        fixed-header
        height="250"
        hide-default-footer
      >
        <!-- パターン名, マニ区分 -->
        <template v-slot:[`item.name`]="{ item }">
          <div
            :title="item.name"
            class="line-clamp one-clamp"
          >
            <small>{{ item.name }}</small>&nbsp;&nbsp;
          </div>
          <div>
            <small>{{ item.manifest_type | short_manifest_type_label }}</small>
          </div>
        </template>
        <!-- /パターン名, マニ区分 -->

        <!-- 取扱品目, 荷姿 -->
        <template v-slot:[`item.item_detail`]="{ item }">
          <div>
            <v-chip
              v-if="item.item_name"
              :title="item.item_name"
              color="#F0E6D0"
              drak
              label
              x-small
              class="chip-ellipsis"
            >
              <small>{{ item.item_name }}</small>
            </v-chip>
          </div>
          <div>
            <small>
              {{ item.packing_name }}&nbsp;全{{ item.item_count }}件
            </small>
          </div>
        </template>
        <!-- /取扱品目, 荷姿 -->

        <!-- 排出事業場,ルート, 処分事業場 -->
        <template v-slot:[`item.route_name`]="{ item }">
          <div class="line-clamp one-clamp">
            <small>{{ item.supplier_location_name || '-' }}</small>
          </div>
          <div
            v-if="item.route_name"
            :title="`${item.route_code}:${item.route_name}`"
            class="line-clamp one-clamp"
          >
            <small>{{ item.route_code }}:{{ item.route_name }}</small>
          </div>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="line-clamp one-clamp"
              >
                <small>{{ item.disposer_location_name }}</small>
              </div>
            </template>
            <div><strong>{{ item.disposer_name }}</strong></div>
            <div>{{ item.disposer_location_name }}</div>
            <div>{{ item.disposer_location_address }}</div>
          </v-tooltip>
        </template>
        <!-- /排出事業場, ルート, 処分事業場 -->

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            color="primary"
            small
            @click="reflectionButtonClickEvent(item)"
          >
            反映
          </v-btn>
        </template>

      </v-data-table>
    </v-card>
  </v-container>
</template>
